import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { 
  ArrowLeft, X, Sparkles, Star, Image, Video, Hash, Users, 
  TrendingUp, Trophy, Crown, Award, Medal, Gift, DollarSign, 
  CheckCircle, Heart, MessageSquare, Camera, Share2, BadgeCheck, 
  Flame, Target, Zap
} from "lucide-react";
import { KudozIcon } from "@/components/KudozIcon";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import KudozDisplay from "@/components/KudozDisplay";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useUserProfileOptional } from "@/hooks/useUserProfile";
import type { User } from "@shared/schema";

interface RewardOption {
  id: string;
  kudozCost: number;
  dollarValue: number;
  title: string;
  description: string;
  rewardType: 'eatzy_gift_credit';
}

const REWARD_OPTIONS: RewardOption[] = [
  {
    id: 'eatzy-20',
    kudozCost: 5000,
    dollarValue: 20,
    title: '$20 Eatzy Gift Credit',
    description: 'Redeem 5,000 Kudoz for $20 credit',
    rewardType: 'eatzy_gift_credit'
  },
  {
    id: 'eatzy-45',
    kudozCost: 10000,
    dollarValue: 45,
    title: '$45 Eatzy Gift Credit',
    description: 'Redeem 10,000 Kudoz for $45 credit',
    rewardType: 'eatzy_gift_credit'
  },
  {
    id: 'eatzy-100',
    kudozCost: 20000,
    dollarValue: 100,
    title: '$100 Eatzy Gift Credit',
    description: 'Redeem 20,000 Kudoz for $100 credit',
    rewardType: 'eatzy_gift_credit'
  }
];

export default function Rewards() {
  const [selectedReward, setSelectedReward] = useState<RewardOption | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const { toast} = useToast();
  
  // Get real Kudoz balance from user profile context (synced with external API)
  const userProfile = useUserProfileOptional();

  const { data: user, isLoading: userLoading } = useQuery<User>({
    queryKey: ['/api/user']
  });

  const redeemMutation = useMutation({
    mutationFn: async (rewardId: string) => {
      const reward = REWARD_OPTIONS.find(r => r.id === rewardId);
      if (!reward) throw new Error('Invalid reward');

      return await fetch('/api/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rewardId: reward.id,
          kudozCost: reward.kudozCost,
          dollarValue: reward.dollarValue,
          rewardType: reward.rewardType
        })
      }).then(res => {
        if (!res.ok) throw new Error('Redemption failed');
        return res.json();
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setSelectedReward(null);
      }, 3000);
    },
    onError: (error: any) => {
      toast({
        title: "Redemption Failed",
        description: error.message || "Unable to redeem reward. Please try again.",
        variant: "destructive"
      });
    }
  });

  const handleRedeemClick = (reward: RewardOption) => {
    // Use real Kudoz balance from profile context (synced with Kudoz API), fallback to user data
    const currentBalance = userProfile?.kudozBalance ?? user?.kudozBalance ?? 0;
    if (!user || currentBalance < reward.kudozCost) {
      toast({
        title: "Insufficient Kudoz",
        description: `You need ${reward.kudozCost.toLocaleString()} Kudoz to redeem this reward.`,
        variant: "destructive"
      });
      return;
    }
    setSelectedReward(reward);
  };

  const confirmRedeem = () => {
    if (!selectedReward) return;
    redeemMutation.mutate(selectedReward.id);
  };

  if (userLoading) {
    return (
      <div className="overflow-y-auto pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-40 bg-muted rounded-lg"></div>
            <div className="h-60 bg-muted rounded-lg"></div>
            <div className="h-80 bg-muted rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  // Use real Kudoz balance from profile context (synced with Kudoz API), fallback to user data
  const kudozBalance = userProfile?.kudozBalance ?? user?.kudozBalance ?? 0;
  const lifetimeKudoz = user?.lifetimeKudozEarned ?? 0;
  const accountBalance = parseFloat(user?.accountBalance || '0');

  return (
    <div className="overflow-y-auto pb-20 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header with back button */}
        <div className="flex items-center justify-between">
          <Link href="/profile">
            <Button variant="ghost" size="icon" data-testid="button-back">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <KudozIcon size="lg" className="w-7 h-7" />
            <h1 className="text-2xl font-bold">Kudoz Rewards</h1>
          </div>
          <Link href="/profile">
            <Button variant="ghost" size="icon" data-testid="button-close">
              <X className="w-5 h-5" />
            </Button>
          </Link>
        </div>

        {/* Subtitle */}
        <div className="text-center -mt-2">
          <p className="text-base md:text-lg font-medium text-kudoz tracking-wide">
            Eat. Review. Share. Earn.
          </p>
        </div>

        {/* Quick Access */}
        <div className="flex justify-center">
          <Link href="/referrals">
            <Card className="hover-elevate active-elevate-2 cursor-pointer" data-testid="card-referrals-link">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">Referrals</p>
                    <p className="text-xs text-muted-foreground">Earn 50 Kudoz per friend</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <Card className="p-6 bg-primary/10 border-primary">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-primary" />
              <div>
                <h3 className="font-semibold text-lg">Redemption Successful!</h3>
                <p className="text-sm text-muted-foreground">
                  ${selectedReward?.dollarValue} has been added to your account balance
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Balance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Kudoz Balance */}
          <Card className="bg-gradient-to-br from-kudoz/10 to-kudoz/5">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-kudoz/20 flex items-center justify-center">
                  <KudozIcon size="lg" className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Current Balance</p>
                  <KudozDisplay amount={kudozBalance} size="lg" showIcon={false} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lifetime Earnings & Account Balance */}
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Lifetime Kudoz Earned</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Trophy className="w-5 h-5 text-primary" />
                    <span className="text-2xl font-bold text-primary">
                      {lifetimeKudoz.toLocaleString()}
                    </span>
                  </div>
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-muted-foreground">Account Balance</p>
                  <p className="text-2xl font-bold text-primary mt-1" data-testid="account-balance">
                    ${accountBalance.toFixed(2)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="earn" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="earn" data-testid="tab-earn">
              <Sparkles className="w-4 h-4 mr-2" />
              Earn Kudoz
            </TabsTrigger>
            <TabsTrigger value="redeem" data-testid="tab-redeem">
              <Gift className="w-4 h-4 mr-2" />
              Redeem Rewards
            </TabsTrigger>
          </TabsList>

          {/* Earn Kudoz Tab */}
          <TabsContent value="earn" className="space-y-6 mt-6">
            {/* Hero Message */}
            <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Welcome to Eatzy Kudoz</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Love sharing your dining experiences? Do your friends always come to you for restaurant 
                      recommendations? Eatzy Kudoz is your chance to get rewarded for your influence! Build your 
                      community, build trust, build your reputation—and earn great rewards along the way.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Core Earning Methods */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-primary" />
                  <span>Share Your Honest Experience</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <EarningMethod
                  icon={<MessageSquare className="w-5 h-5 text-blue-500" />}
                  title="Write a Review"
                  kudoz={10}
                  description="Base reward for sharing your experience"
                />
                <EarningMethod
                  icon={<Image className="w-5 h-5 text-green-500" />}
                  title="Add Photos"
                  kudoz={5}
                  description="+5 Kudoz per photo—show off your meal"
                />
                <EarningMethod
                  icon={<Video className="w-5 h-5 text-purple-500" />}
                  title="Upload Videos"
                  kudoz={10}
                  description="+10 Kudoz per video—create engaging content"
                />
                <EarningMethod
                  icon={<Flame className="w-5 h-5 text-orange-500" />}
                  title="Rate the Vibe"
                  kudoz={5}
                  description="Score the restaurant atmosphere (1-10 scale)"
                />
                <EarningMethod
                  icon={<Star className="w-5 h-5 text-yellow-500" />}
                  title="Rate Ordered Items"
                  kudoz={5}
                  description="+5 Kudoz per item rated from your order"
                />
              </CardContent>
            </Card>

            {/* Sharing & Social */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-primary" />
                  <span>Share Your Reviews</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <EarningMethod
                  icon={<Users className="w-5 h-5 text-blue-500" />}
                  title="Share with Friends"
                  kudoz={5}
                  description="Share your review with friends only"
                />
                <EarningMethod
                  icon={<BadgeCheck className="w-5 h-5 text-green-500" />}
                  title="Make Public"
                  kudoz={10}
                  description="Make your review visible to everyone"
                />
                <EarningMethod
                  icon={<Share2 className="w-5 h-5 text-purple-500" />}
                  title="Publish to Social Media"
                  kudoz={20}
                  description="Share your review on Instagram, Facebook, or Twitter"
                  badge="Bonus"
                />
              </CardContent>
            </Card>

            {/* Community & Referrals */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  <span>Build Your Community</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <EarningMethod
                  icon={<Gift className="w-5 h-5 text-primary" />}
                  title="Invite Friends"
                  kudoz={50}
                  description="Tell your friends about Eatzy and earn big rewards"
                  badge="Big Bonus"
                />
                <EarningMethod
                  icon={<Heart className="w-5 h-5 text-rose-500" />}
                  title="Get Likes & Engagement"
                  kudoz={3}
                  description="Quality content earns likes—more engagement means more Kudoz"
                />
                <EarningMethod
                  icon={<Users className="w-5 h-5 text-indigo-500" />}
                  title="Grow Your Following"
                  kudoz={10}
                  description="Build your follower base and increase your earning multiplier"
                  badge="Multiplier"
                />
              </CardContent>
            </Card>

            {/* Trust & Reputation */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BadgeCheck className="w-5 h-5 text-primary" />
                  <span>Increase Your Trust Score</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <EarningMethod
                  icon={<TrendingUp className="w-5 h-5 text-green-600" />}
                  title="Higher Trust Rating"
                  kudoz={0}
                  description="Earn 1.5-2x Kudoz multiplier on all activities as you build trust"
                  badge="2x Multiplier"
                />
                <EarningMethod
                  icon={<Target className="w-5 h-5 text-blue-600" />}
                  title="Consistent Quality"
                  kudoz={15}
                  description="Maintain a streak of helpful reviews to unlock bonus Kudoz"
                  badge="Streak"
                />
                <EarningMethod
                  icon={<Flame className="w-5 h-5 text-orange-600" />}
                  title="Elite Status"
                  kudoz={0}
                  description="Reach Elite tier to unlock VIP perks and secret menus"
                  badge="VIP"
                />
              </CardContent>
            </Card>

            {/* Ordering Benefits */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-primary" />
                  <span>Support Local Restaurants</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <EarningMethod
                  icon={<DollarSign className="w-5 h-5 text-green-600" />}
                  title="Order Through Eatzy"
                  kudoz={7}
                  description="Earn 7 Kudoz per dollar spent—support local restaurants and get rewarded"
                  badge="7 per $"
                />
                <EarningMethod
                  icon={<Camera className="w-5 h-5 text-purple-600" />}
                  title="Post-Order Reviews"
                  kudoz={35}
                  description="Complete a full post-order review with photos for maximum Kudoz"
                  badge="Bonus"
                />
              </CardContent>
            </Card>

            {/* Benefits Section */}
            <Card className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 border-purple-200 dark:border-purple-800">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Crown className="w-10 h-10 text-purple-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-lg mb-3">Influencers: Get Rewarded for Your Influence!</h3>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p className="flex items-start gap-2">
                        <Award className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                        <span>Unlock secret menus and VIP treatment at participating restaurants</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <Trophy className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                        <span>Higher trust scores unlock bigger Kudoz multipliers on every action</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <Medal className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                        <span>Large followings earn bonus Kudoz automatically with each review</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                        <span>The more you share, the more you earn—it's that simple!</span>
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Redeem Rewards Tab */}
          <TabsContent value="redeem" className="space-y-6 mt-6">
            {/* Account Balance Display */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <DollarSign className="w-7 h-7 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Your Account Balance</p>
                    <p className="text-2xl font-bold text-primary mt-1">
                      ${accountBalance.toFixed(2)}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-3">
                  Gift credits are applied to your account balance which you can use at checkout on your next order.
                </p>
              </CardContent>
            </Card>

            {/* Available Rewards */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Available Rewards</h2>
              
              {REWARD_OPTIONS.map((reward) => {
                const canAfford = kudozBalance >= reward.kudozCost;
                const isSelected = selectedReward?.id === reward.id;

                return (
                  <Card 
                    key={reward.id}
                    className={cn(
                      "p-6 transition-all cursor-pointer",
                      isSelected && "ring-2 ring-primary",
                      canAfford ? "hover-elevate active-elevate-2" : "opacity-60"
                    )}
                    onClick={() => canAfford && handleRedeemClick(reward)}
                    data-testid={`reward-option-${reward.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center flex-shrink-0">
                        <Gift className="w-6 h-6 text-white" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <h3 className="font-semibold text-lg">{reward.title}</h3>
                          {!canAfford && (
                            <Badge variant="secondary" className="text-xs">
                              Need {(reward.kudozCost - kudozBalance).toLocaleString()} more
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-3">
                          {reward.description}
                        </p>

                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <KudozIcon size="sm" />
                            <span className="font-semibold text-kudoz">
                              {reward.kudozCost.toLocaleString()}
                            </span>
                          </div>
                          <span className="text-muted-foreground">→</span>
                          <div className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4 text-primary" />
                            <span className="font-semibold text-primary">
                              {reward.dollarValue}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Confirmation Dialog */}
            {selectedReward && !showSuccess && (
              <Card className="p-6 border-primary">
                <h3 className="font-semibold text-lg mb-4">Confirm Redemption</h3>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Reward</span>
                    <span className="font-semibold">{selectedReward.title}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Cost</span>
                    <span className="font-semibold text-kudoz">
                      {selectedReward.kudozCost.toLocaleString()} Kudoz
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">You'll receive</span>
                    <span className="font-semibold text-primary">
                      ${selectedReward.dollarValue} credit
                    </span>
                  </div>
                  <div className="border-t pt-3 flex justify-between">
                    <span className="text-muted-foreground">New Kudoz balance</span>
                    <span className="font-semibold">
                      {(kudozBalance - selectedReward.kudozCost).toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setSelectedReward(null)}
                    disabled={redeemMutation.isPending}
                    data-testid="button-cancel-redeem"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={confirmRedeem}
                    disabled={redeemMutation.isPending}
                    data-testid="button-confirm-redeem"
                  >
                    {redeemMutation.isPending ? 'Redeeming...' : 'Confirm Redemption'}
                  </Button>
                </div>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

interface EarningMethodProps {
  icon: React.ReactNode;
  title: string;
  kudoz: number;
  description: string;
  badge?: string;
}

function EarningMethod({ icon, title, kudoz, description, badge }: EarningMethodProps) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg hover-elevate">
      <div className="flex-shrink-0 mt-0.5">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-medium">{title}</h4>
          {badge && (
            <Badge variant="secondary" className="text-xs">
              {badge}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {kudoz > 0 && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="font-semibold text-kudoz">+{kudoz}</span>
          <KudozIcon size="sm" />
        </div>
      )}
    </div>
  );
}
